import React, { useState } from "react";
import {
  ElementsConsumer,
  useStripe,
  useElements,
  PaymentElement,
  LinkAuthenticationElement,
  AddressElement,
} from "@stripe/react-stripe-js";

import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { createPayment } from "../../store/payments/service";
import { getLocalState } from "../../utils/localStorage";

// Composant Modal de Succès
const SuccessModal = ({ isOpen, onClose, amount, credits }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-2xl p-8 max-w-md w-full mx-4 animate-fade-in">
        <div className="text-center">
          {/* Icône de succès */}
          <div className="mx-auto flex items-center justify-center h-20 w-20 rounded-full bg-green-100 mb-4">
            <svg
              className="h-12 w-12 text-green-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>

          {/* Titre */}
          <h3 className="text-2xl font-bold text-gray-900 mb-4">
            Paiement réussi !
          </h3>

          {/* Message */}
          <p className="text-gray-600 mb-2">
            Votre paiement de a été effectué avec succès
          </p>

          {credits && (
            <p className="text-lg text-green-600 font-semibold mb-2">
              Vos credit on ete ajoute a votre compte
            </p>
          )}

          {/* Message de remerciement */}
          <p className="text-lg text-gray-700 font-semibold mb-6">
            Legafrik vous remercie
          </p>

          {/* Bouton de fermeture */}
          <button
            onClick={onClose}
            className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-6 rounded-lg transition duration-200"
          >
            Continuer
          </button>
        </div>
      </div>
    </div>
  );
};

export const CheckoutForm = (props) => {
  const { dataPaiement, paymentData } = props;
  const useModal = useSelector((state) => state?.modal);
  const stripe = useStripe();
  const elements = useElements();
  const [message] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState(0);
  const [creditsObtained, setCreditsObtained] = useState(0);
  const currentUser = getLocalState("_currentUser");

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setIsProcessing(true);

    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: `${window.location.origin}/facturations`,
      },
      redirect: "if_required",
    });

    if (error) {
      toast.error(error?.message);
      setIsProcessing(false);
    } else if (paymentIntent && paymentIntent.status === "succeeded") {
      const data = {
        pack: dataPaiement.id,
        account: currentUser.user_id,
        payment: paymentData.response.payment.id,
        payment_intent: {
          id: paymentData.response.payment.payment_data.payment_intent_id,
          status: paymentData.response.payment.status,
        },
      };

      const results = await createPayment(data);

      if (results.status === 201 && results.statusText === "Created") {
        // Stocker les informations pour le modal
        setPaymentAmount(dataPaiement.amount ?? useModal?.data?.total_amount);
        setCreditsObtained(dataPaiement?.credits || 1);

        // Afficher le modal de succès
        setShowSuccessModal(true);
        setIsProcessing(false);
      }
    }
  };

  const handleCloseSuccessModal = () => {
    setShowSuccessModal(false);
    window.location.href = `${window.location.origin}/facturations`;
  };

  return (
    <div>
      <SuccessModal
        isOpen={showSuccessModal}
        onClose={handleCloseSuccessModal}
        amount={paymentAmount}
        credits={creditsObtained}
      />

      <div className="product-info">
        <h4 className="product-price">
          {dataPaiement.amount ?? useModal?.data?.total_amount} FCFA
        </h4>
      </div>
      <form id="payment-form" onSubmit={handleSubmit}>
        <h3 className="text-lg font-medium">Informations</h3>

        <LinkAuthenticationElement />
        <AddressElement options={{ mode: "shipping" }} />
        <h3 className="text-lg font-medium">Paiement</h3>

        <PaymentElement id="payment-element" />

        <button
          className="btn-pay"
          type="submit"
          disabled={isProcessing || !stripe || !elements}
        >
          {isProcessing ? "Paiement en cours..." : "Payer maintenant"}
        </button>
        {message && (
          <div
            id="payment-message"
            className="text-red-600 text-2xl font-bold my-8 text-center"
          >
            {message}
          </div>
        )}
      </form>
    </div>
  );
};

export default function InjectedCheckoutForm(props) {
  const { dataPaiement, paymentData } = props;

  return (
    <ElementsConsumer>
      {({ stripe, elements }) => (
        <CheckoutForm
          nextLink={props?.nextLink}
          stripe={stripe}
          elements={elements}
          dataPaiement={dataPaiement}
          paymentData={paymentData}
        />
      )}
    </ElementsConsumer>
  );
}